import {
    LitElement,
    css,
    html,
    customElement,
    property,
} from "@/wc/registry/lib/framework/lit/index.js";

const check = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    class="bi bi-check"
    viewBox="0 0 16 16"
>
    <path
        d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"
    />
</svg>`;

@customElement("c-form-button")
export default class FormButton extends LitElement {
    static styles = css`
        :host {
            --stekz-primary-button-text-color: white;
            --stekz-primary-button-color: #842cff;
            --stekz-font-family: "Plus Jakarta Sans";
            --stekz-font-size: 11.333px;
            --stekz-button-font-weight: bold;

            --button-color: var(
                --primary-button-color,
                var(--stekz-primary-button-color)
            );
        }

        button {
            position: relative;
            border: 1px solid transparent;
            border-radius: 0.375em;
            color: var(
                --primary-button-text-color,
                var(--stekz-primary-button-text-color)
            );
            height: 2.5em;
            box-shadow: none;
            cursor: pointer;
            font-family: var(--font-family, var(--stekz-font-family));
            font-size: var(
                --button-font-size,
                var(--font-size, var(--stekz-font-size))
            );
            background-color: var(--button-color);
            font-weight: var(
                --button-font-weight,
                var(--stekz-button-font-weight)
            );
        }

        button:hover {
            /* Just a trick to avoid having to define another variable for a darker
      * shade of the button-color */
            filter: brightness(0.8);
        }

        .loading {
            opacity: 0.8;
            pointer-events: none;
        }

        .loading span {
            visibility: hidden;
            opacity: 0;
        }

        /* Credits to https://stephanwagner.me/only-css-loading-spinner */
        @keyframes spinner {
            to {
                transform: rotate(360deg);
            }
        }

        .loading:before {
            content: "";
            box-sizing: border-box;
            position: absolute;
            top: 50%;
            left: 50%;
            width: 20px;
            height: 20px;
            margin-top: -10px;
            margin-left: -10px;
            border-radius: 50%;
            border: 2px solid #ccc;
            border-top-color: var(
                --primary-loading-button-color,
                var(--primary-button-color, var(--stekz-primary-button-color))
            );
            animation: spinner 0.6s linear infinite;
        }

        button.inline {
            background-color: var(
                --default-white-color,
                var(--stekz-default-white-color)
            );
            border-color: var(
                --stekz-primary-color,
                var(--primary-color, var(--stekz-default-primary-color))
            );
            color: var(
                --stekz-primary-color,
                var(--primary-color, var(--stekz-default-primary-color))
            );
            margin: 0;
            border-radius: 3px;
            border-width: 1px;
            border-style: solid;
            cursor: pointer;
        }

        button.inline {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        button.inline:not(.compact) {
            height: 33.2667px;
            width: 33px;
        }
        button.inline.compact {
            height: 26px;
            width: 26px;
        }

        button.inline.compact > svg {
            align-self: center;
        }

        button:not(.compact):not(.inline) {
            padding-left: 1em;
            padding-right: 1em;
        }
    `;

    @property()
    loading = false;
    @property({ attribute: "data-button-text" })
    buttonText = "Submit";
    @property({
        attribute: "data-inline",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    inline = false;
    @property({
        attribute: "data-compact",
        converter: {
            fromAttribute: (value: string) => {
                if (value === "false") {
                    return false;
                }

                return true;
            },
        },
    })
    compact = false;

    inlineButtonTemplate() {
        if (this.loading) {
            return html`<button
                class="inline loading ${this.compact ? "compact" : ""}"
                disabled
            ></button>`;
        }

        return html`<button class="inline ${this.compact ? "compact" : ""}">
            ${check}
        </button>`;
    }

    render() {
        if (this.inline) {
            return this.inlineButtonTemplate();
        }

        if (this.loading) {
            return html`<button class="loading" disabled>
                <span>${this.buttonText}</span>
            </button>`;
        }

        return html`<button>${this.buttonText}</button>`;
    }

    connectedCallback() {
        super.connectedCallback();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-form-button": FormButton;
    }
}
